import axios, { AxiosInstance } from 'axios';
import { platformAuthHeaders, setPlatformSessionToken } from './auth.service';

function resolveApiBaseUrl(): string {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    return envUrl.endsWith('/api') ? envUrl : envUrl.replace(/\/+$/, '') + '/api';
  }
  const host = window.location.hostname.toLowerCase();
  if (host.endsWith('.electemb.com') || host === 'electemb.com') {
    const proto = window.location.protocol;
    return `${proto}//hrm-api.electemb.com/api`;
  }
  return `http://${host}:5082/api`;
}

/** The API's origin without the /api suffix — where the Live Monitor's /v1 control-plane routes live. */
export function resolveApiOrigin(): string {
  return resolveApiBaseUrl().replace(/\/api$/, '');
}

class PlatformApiClient {
  private client: AxiosInstance;
  private _redirecting = false;

  constructor() {
    this.client = axios.create({
      baseURL: resolveApiBaseUrl(),
      headers: { 'Content-Type': 'application/json' },
      // The platform session cookie rides every request; the X-Session marker below tells the
      // server it is the PLATFORM cookie it should read, not an HRMS one in the same browser.
      withCredentials: true,
    });

    this.client.interceptors.request.use((config) => {
      Object.assign(config.headers, platformAuthHeaders());
      if (config.data instanceof FormData) delete config.headers['Content-Type'];
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (this._redirecting) return Promise.reject(error);
        const status = error.response?.status;
        // Only a platform screen is sent to the platform login. A stale platform cookie found
        // while an HRMS user boots is simply not a platform session.
        if (status === 401 && window.location.hash.startsWith('#/platform')) {
          this._redirecting = true;
          setPlatformSessionToken(null);
          window.location.replace(`${window.location.pathname}#/platform/login?reason=session_expired`);
        }
        return Promise.reject(error);
      }
    );
  }

  get<T>(url: string, config = {}) { return this.client.get<T>(url, config); }
  post<T>(url: string, data?: any, config = {}) { return this.client.post<T>(url, data, config); }
  put<T>(url: string, data?: any, config = {}) { return this.client.put<T>(url, data, config); }
  patch<T>(url: string, data?: any, config = {}) { return this.client.patch<T>(url, data, config); }
  delete<T>(url: string, config = {}) { return this.client.delete<T>(url, config); }
}

export const platformApiClient = new PlatformApiClient();
